document.addEventListener("DOMContentLoaded", () => {
    const toggles = document.querySelectorAll(".collapse-toggle");
    if (toggles.length > 0) {
        new CollapseToggle();
    }
});

export default class CollapseToggle {
    constructor() {
        this.init();
    }

    // ========================================
    // LIFECYCLE METHODS
    // ========================================

    init() {
        document.addEventListener("click", (event) => this.handleToggleClick(event));

        // Initialize any elements that start in the open state
        this.initializeOpenStates();
    }

    initializeOpenStates() {
        // Find all collapse-pushdown elements that are already open
        const openPushdowns = document.querySelectorAll(".collapse-pushdown.is-open");

        openPushdowns.forEach(element => {
            // Set the max-height to the content height without animation
            element.style.maxHeight = element.scrollHeight + "px";

            // Then set to 'none' so nested content can expand
            // Use setTimeout to ensure the initial height is set first
            setTimeout(() => {
                if (element.classList.contains("is-open")) {
                    element.style.maxHeight = "none";
                }
            }, 50);
        });

        // Handle desktop-open pattern: start open on desktop, closed on mobile
        const desktopOpenElements = document.querySelectorAll(".collapse-pushdown.collapse-desktop-open");

        desktopOpenElements.forEach(element => {
            // On desktop, add is-open class and initialize
            if (window.innerWidth >= 768) {
                element.classList.add("is-open");

                // Find the associated toggle and update its state
                const toggles = document.querySelectorAll(`[data-target="#${element.id}"]`);
                toggles.forEach(toggle => {
                    toggle.classList.add("active");
                    toggle.setAttribute("aria-expanded", "true");
                });

                // Set the max-height
                element.style.maxHeight = element.scrollHeight + "px";

                setTimeout(() => {
                    if (element.classList.contains("is-open")) {
                        element.style.maxHeight = "none";
                    }
                }, 50);
            }
            // On mobile, stays collapsed (no action needed)
        });
    }

    // ========================================
    // EVENT BINDING METHODS
    // ========================================

    handleToggleClick(event) {
        // Check if the clicked element is a collapse toggle
        const toggle = event.target.closest(".collapse-toggle");
        if (!toggle) return;

        // Skip if the toggle is hidden (respects Bootstrap visibility helpers)
        if (this.isElementHidden(toggle)) return;

        // Prevent default anchor behavior if a link is used
        event.preventDefault();

        // Get the target selector from the data attribute
        const targetSelector = toggle.getAttribute("data-target");
        const targetElement = document.querySelector(targetSelector);

        if (!targetElement || this.isElementHidden(targetElement)) return;

        this.toggleContent(toggle, targetElement);
    }

    // ========================================
    // HELPER & UTILITY METHODS
    // ========================================

    isElementHidden(element) {
        // Check if element or any parent has display:none or visibility:hidden
        const style = window.getComputedStyle(element);
        return style.display === "none" || style.visibility === "hidden";
    }

    toggleContent(toggle, targetElement) {
        // Toggle state indicators
        const isOpen = targetElement.classList.toggle("is-open");

        // Update accessibility attributes
        toggle.setAttribute("aria-expanded", isOpen);

        // Add active class to toggle for styling (e.g., caret rotation)
        if (isOpen) {
            toggle.classList.add("active");
        } else {
            toggle.classList.remove("active");
        }

        // Handle height transition for push-down behavior (not overlay)
        // Check if targetElement has 'collapse-pushdown' class for accordion-style behavior
        if (targetElement.classList.contains("collapse-pushdown")) {
            if (isOpen) {
                this.openPushdown(targetElement);
            } else {
                this.closePushdown(targetElement);
            }
        }
    }

    openPushdown(targetElement) {
        // Remove any existing transition end listener
        if (targetElement._collapseTransitionHandler) {
            targetElement.removeEventListener("transitionend", targetElement._collapseTransitionHandler);
        }

        // Force a reflow to ensure the transition animates
        if (!targetElement.style.maxHeight || targetElement.style.maxHeight === "0px") {
            targetElement.style.maxHeight = "0px";
            void targetElement.offsetHeight;
        }
        
        // Set the target height for smooth transition
        const targetHeight = targetElement.scrollHeight + "px";
        targetElement.style.maxHeight = targetHeight;

        // After transition completes, remove max-height so nested content can expand freely
        targetElement._collapseTransitionHandler = (e) => {
            if (e.propertyName === "max-height" && targetElement.classList.contains("is-open")) {
                targetElement.style.maxHeight = "none";
                if (targetElement._collapseTransitionHandler) {
                    targetElement.removeEventListener("transitionend", targetElement._collapseTransitionHandler);
                    delete targetElement._collapseTransitionHandler;
                }
            }
        };
        
        targetElement.addEventListener("transitionend", targetElement._collapseTransitionHandler);
    }

    closePushdown(targetElement) {
        // Remove transition listener if present
        if (targetElement._collapseTransitionHandler) {
            targetElement.removeEventListener("transitionend", targetElement._collapseTransitionHandler);
            delete targetElement._collapseTransitionHandler;
        }

        // If max-height is 'none', we need to set it to a specific value first
        if (targetElement.style.maxHeight === "none" || !targetElement.style.maxHeight) {
            // Get the current actual height
            const currentHeight = targetElement.scrollHeight + "px";
            targetElement.style.maxHeight = currentHeight;
            // Force reflow
            void targetElement.offsetHeight;
        }

        // Now animate to 0
        targetElement.style.maxHeight = "0";
    }
}
